import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { Phone, MessageCircle, Mail, MapPin, Clock, CheckCircle, Upload, X, FileImage } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const KontaktPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: ""
  });
  const [files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (selectedFiles) => {
    const validFiles = Array.from(selectedFiles).filter(file => {
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
      const maxSize = 10 * 1024 * 1024; // 10MB
      return validTypes.includes(file.type) && file.size <= maxSize;
    });
    
    // Limit to 5 files
    const newFiles = [...files, ...validFiles].slice(0, 5);
    setFiles(newFiles);
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      if (files.length > 0) {
        // Submit with files using FormData
        const formDataToSend = new FormData();
        formDataToSend.append('name', formData.name);
        formDataToSend.append('email', formData.email);
        formDataToSend.append('phone', formData.phone);
        formDataToSend.append('message', formData.message);
        if (formData.service) {
          formDataToSend.append('service', formData.service);
        }
        files.forEach(file => {
          formDataToSend.append('files', file);
        });
        
        await axios.post(`${API}/contact-with-files`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        // Submit without files
        await axios.post(`${API}/contact`, formData);
      }
      
      setSubmitted(true);
      setFormData({ name: "", email: "", phone: "", service: "", message: "" });
      setFiles([]);
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO 
        title="Kontakt - Schaden melden"
        description="Kontaktieren Sie Kfz Gutachter Schmeißer: ☎ 0174 893 6514 oder WhatsApp. Täglich 9-23 Uhr erreichbar. Innerhalb 60 Min. vor Ort in Hamburg & Schleswig-Holstein."
        canonical="/kontakt"
        keywords="Kfz Gutachter Kontakt, Schaden melden, Hamburg, Schleswig-Holstein, Unfallgutachten"
      />
      {/* Hero */}
      <section className="bg-[#F7F7F7] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block text-[#AA6622] font-bold text-sm uppercase tracking-wider mb-4">Kontakt</span>
            <h1 className="text-4xl sm:text-5xl font-black text-black mb-6 font-['Outfit']">
              Schaden melden – wir helfen sofort
            </h1>
            <p className="text-lg text-[#4A4A4A] leading-relaxed">
              Ob telefonisch, per WhatsApp oder über das Kontaktformular – wir sind täglich von 9:00 bis 23:00 Uhr für Sie erreichbar. Bei einem Unfall zählt jede Minute – kontaktieren Sie uns jetzt!
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-2xl font-bold text-black mb-8 font-['Outfit']">Direkter Kontakt</h2>
              
              {/* Contact Methods */}
              <div className="space-y-6 mb-12">
                <a href="tel:+491748936514" className="flex items-center space-x-4 p-6 bg-[#F7F7F7] rounded-sm hover:shadow-md transition-shadow group" data-testid="contact-phone-link">
                  <div className="w-14 h-14 bg-[#AA6622] rounded-sm flex items-center justify-center group-hover:scale-105 transition-transform">
                    <Phone className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-black text-lg">Telefon</div>
                    <div className="text-[#AA6622] text-xl font-bold">0174 893 6514</div>
                    <div className="text-sm text-[#4A4A4A]">Sofortige Hilfe bei Unfällen</div>
                  </div>
                </a>

                <a href="https://wa.me/+491748936514" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-4 p-6 bg-[#F7F7F7] rounded-sm hover:shadow-md transition-shadow group" data-testid="contact-whatsapp-link">
                  <div className="w-14 h-14 bg-[#25D366] rounded-sm flex items-center justify-center group-hover:scale-105 transition-transform">
                    <MessageCircle className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-black text-lg">WhatsApp</div>
                    <div className="text-[#25D366] text-xl font-bold">Jetzt chatten</div>
                    <div className="text-sm text-[#4A4A4A]">Schnell Fotos & Infos senden</div>
                  </div>
                </a>

                <a href="mailto:info@bis-kfz-gutachten.de" className="flex items-center space-x-4 p-6 bg-[#F7F7F7] rounded-sm hover:shadow-md transition-shadow group" data-testid="contact-email-link">
                  <div className="w-14 h-14 bg-black rounded-sm flex items-center justify-center group-hover:scale-105 transition-transform">
                    <Mail className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-black text-lg">E-Mail</div>
                    <div className="text-black text-lg">info@bis-kfz-gutachten.de</div>
                    <div className="text-sm text-[#4A4A4A]">Für Anfragen & Dokumente</div>
                  </div>
                </a>
              </div>

              {/* Info Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-[#F7F7F7] p-6 rounded-sm">
                  <Clock className="w-8 h-8 text-[#AA6622] mb-3" />
                  <h3 className="font-bold text-black mb-2">Erreichbarkeit</h3>
                  <p className="text-[#4A4A4A]">Mo – So<br /><strong>09:00 – 23:00 Uhr</strong></p>
                </div>
                <div className="bg-[#F7F7F7] p-6 rounded-sm">
                  <MapPin className="w-8 h-8 text-[#AA6622] mb-3" />
                  <h3 className="font-bold text-black mb-2">Einsatzgebiet</h3>
                  <p className="text-[#4A4A4A]">Hamburg &<br /><strong>Schleswig-Holstein</strong></p>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-[#F7F7F7] p-8 rounded-sm">
              {submitted ? (
                <div className="text-center py-12" data-testid="contact-success">
                  <CheckCircle className="w-20 h-20 text-[#25D366] mx-auto mb-6" />
                  <h3 className="text-2xl font-bold text-black mb-4 font-['Outfit']">Nachricht gesendet!</h3>
                  <p className="text-[#4A4A4A] mb-8">Vielen Dank für Ihre Nachricht. Wir melden uns schnellstmöglich bei Ihnen.</p>
                  <Button onClick={() => setSubmitted(false)} variant="outline" className="border-2 border-black text-black hover:bg-black hover:text-white">
                    Neue Nachricht senden
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form">
                  <h3 className="text-2xl font-bold text-black mb-6 font-['Outfit']">Schaden melden</h3>
                  
                  <div className="space-y-5">
                    <div>
                      <Label htmlFor="name" className="text-black font-medium">Name *</Label>
                      <Input
                        id="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="mt-2 bg-white border-[#E5E5E5] focus:border-[#AA6622] focus:ring-[#AA6622]"
                        placeholder="Ihr vollständiger Name"
                        data-testid="contact-input-name"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="email" className="text-black font-medium">E-Mail *</Label>
                        <Input
                          id="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="mt-2 bg-white border-[#E5E5E5] focus:border-[#AA6622] focus:ring-[#AA6622]"
                          placeholder="ihre@email.de"
                          data-testid="contact-input-email"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone" className="text-black font-medium">Telefon *</Label>
                        <Input
                          id="phone"
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="mt-2 bg-white border-[#E5E5E5] focus:border-[#AA6622] focus:ring-[#AA6622]"
                          placeholder="0174 123 4567"
                          data-testid="contact-input-phone"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="service" className="text-black font-medium">Gewünschte Leistung</Label>
                      <Select value={formData.service} onValueChange={(value) => setFormData({ ...formData, service: value })}>
                        <SelectTrigger className="mt-2 bg-white border-[#E5E5E5] focus:border-[#AA6622] focus:ring-[#AA6622]" data-testid="contact-select-service">
                          <SelectValue placeholder="Bitte wählen" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unfallgutachten">Unfallgutachten</SelectItem>
                          <SelectItem value="haftpflichtgutachten">Haftpflichtgutachten</SelectItem>
                          <SelectItem value="kasko-schaden">Kasko-Schaden</SelectItem>
                          <SelectItem value="kostenvoranschlag">Kostenvoranschlag</SelectItem>
                          <SelectItem value="wertgutachten">Wertgutachten</SelectItem>
                          <SelectItem value="oldtimer-gutachten">Oldtimer-Gutachten</SelectItem>
                          <SelectItem value="sonstiges">Sonstiges</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-black font-medium">Ihre Nachricht *</Label>
                      <Textarea
                        id="message"
                        required
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        className="mt-2 bg-white border-[#E5E5E5] focus:border-[#AA6622] focus:ring-[#AA6622] min-h-[140px]"
                        placeholder="Beschreiben Sie kurz den Schaden oder Ihr Anliegen..."
                        data-testid="contact-input-message"
                      />
                    </div>

                    {/* File Upload Area */}
                    <div>
                      <Label className="text-black font-medium">Fotos hochladen (optional)</Label>
                      <input
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        multiple
                        accept=".jpg,.jpeg,.png,.gif,.webp,.pdf"
                        onChange={(e) => handleFileSelect(e.target.files)}
                        data-testid="contact-file-input"
                      />
                      <div
                        className={`mt-2 border-2 border-dashed rounded-sm p-6 text-center cursor-pointer transition-colors ${
                          dragOver 
                            ? 'border-[#AA6622] bg-[#AA6622]/5' 
                            : 'border-[#E5E5E5] hover:border-[#AA6622]/50'
                        }`}
                        onClick={() => fileInputRef.current?.click()}
                        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={(e) => { 
                          e.preventDefault(); 
                          setDragOver(false); 
                          handleFileSelect(e.dataTransfer.files);
                        }}
                        data-testid="contact-file-upload"
                      >
                        <Upload className="w-10 h-10 text-[#AA6622] mx-auto mb-3" />
                        <p className="text-[#4A4A4A]">
                          <span className="text-[#AA6622] font-medium">Dateien auswählen</span> oder per Drag & Drop ablegen
                        </p>
                        <p className="text-sm text-[#4A4A4A]/70 mt-2">JPG, PNG, PDF bis 10 MB (max. 5 Dateien)</p>
                      </div>
                      
                      {/* File Preview */}
                      {files.length > 0 && (
                        <div className="mt-4 space-y-2">
                          {files.map((file, index) => (
                            <div 
                              key={index}
                              className="flex items-center justify-between bg-white border border-[#E5E5E5] rounded-sm px-4 py-3"
                            >
                              <div className="flex items-center space-x-3">
                                <FileImage className="w-5 h-5 text-[#AA6622]" />
                                <div>
                                  <p className="text-sm font-medium text-black truncate max-w-[200px]">{file.name}</p>
                                  <p className="text-xs text-[#4A4A4A]">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeFile(index)}
                                className="p-1 hover:bg-red-50 rounded-full transition-colors"
                                data-testid={`remove-file-${index}`}
                              >
                                <X className="w-5 h-5 text-red-500" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#AA6622] hover:bg-[#8e541b] text-white font-bold py-4 rounded-sm text-lg h-auto"
                      data-testid="contact-form-submit"
                    >
                      {isSubmitting ? "Wird gesendet..." : "Nachricht senden"}
                    </Button>

                    <p className="text-sm text-[#4A4A4A] text-center">
                      Mit dem Absenden stimmen Sie unserer <Link to="/datenschutz" className="text-[#AA6622] hover:underline">Datenschutzerklärung</Link> zu.
                    </p>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Quick Help */}
      <section className="py-20 bg-[#F7F7F7]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-black mb-6 font-['Outfit']">Unfall gehabt? So geht's weiter:</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
            <div className="bg-white p-6 rounded-sm">
              <div className="text-3xl font-black text-[#AA6622]/20 mb-2 font-['Outfit']">01</div>
              <h3 className="font-bold text-black mb-2">Rufen Sie uns an</h3>
              <p className="text-[#4A4A4A] text-sm">Wir besprechen Ihren Fall und klären die nächsten Schritte.</p>
            </div>
            <div className="bg-white p-6 rounded-sm">
              <div className="text-3xl font-black text-[#AA6622]/20 mb-2 font-['Outfit']">02</div>
              <h3 className="font-bold text-black mb-2">Wir kommen zu Ihnen</h3>
              <p className="text-[#4A4A4A] text-sm">Innerhalb von 60 Minuten sind wir vor Ort zur Begutachtung.</p>
            </div>
            <div className="bg-white p-6 rounded-sm">
              <div className="text-3xl font-black text-[#AA6622]/20 mb-2 font-['Outfit']">03</div>
              <h3 className="font-bold text-black mb-2">Gutachten erhalten</h3>
              <p className="text-[#4A4A4A] text-sm">In 24h haben Sie Ihr Gutachten – wir regeln den Rest.</p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default KontaktPage;
